import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, Loader2, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const evaluationId = searchParams.get('evaluation');
  const [processing, setProcessing] = useState(true);
  const [attempts, setAttempts] = useState(0);
  const [reprocessing, setReprocessing] = useState(false);
  const [needsReprocess, setNeedsReprocess] = useState(false);
  const maxAttempts = 10;

  useEffect(() => {
    if (!evaluationId) {
      navigate('/');
      return;
    }

    checkPaymentProcessed();
  }, [evaluationId, navigate]);

  const checkPaymentProcessed = async () => {
    try {
      // Poll for up to 30 seconds to see if webhook processed the payment
      const interval = setInterval(async () => {
        setAttempts(prev => prev + 1);

        const { data, error } = await supabase
          .from('evaluations')
          .select('insights')
          .eq('id', evaluationId)
          .single();

        if (error) {
          console.error('Error checking payment:', error);
          return;
        }

        // Check if insights are populated and complete
        if (data?.insights) {
          const insights = data.insights as any;
          const hasCompleteInsights = insights?.categories && insights?.trends && insights?.suggestions;
          
          clearInterval(interval);
          setProcessing(false);
          
          if (hasCompleteInsights) {
            toast.success('Premium insights unlocked!');
            setTimeout(() => {
              navigate(`/result?evaluation=${evaluationId}`);
            }, 2000);
          } else {
            // Insights exist but are incomplete
            console.warn('Insights are incomplete:', insights);
            setNeedsReprocess(true);
            toast.warning('Premium insights need to be regenerated.');
          }
        }

        // Stop after max attempts
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          setProcessing(false);
          setNeedsReprocess(true);
          toast.warning('Taking longer than expected. You can regenerate your premium insights.');
        }
      }, 3000);

      return () => clearInterval(interval);
    } catch (error) {
      console.error('Error:', error);
      setProcessing(false);
    }
  };

  const handleReprocess = async () => {
    if (!evaluationId) return;
    
    setReprocessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('reprocess-premium-insights', {
        body: { evaluationId, language: 'pt' }
      });

      if (error) {
        throw error;
      }

      toast.success('Premium insights regenerated successfully!');
      setTimeout(() => {
        navigate(`/result?evaluation=${evaluationId}`);
      }, 1500);
    } catch (error) {
      console.error('Error reprocessing insights:', error);
      toast.error('Failed to regenerate insights. Please try again.');
    } finally {
      setReprocessing(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-green-50 via-background to-green-100 dark:from-green-950/20 dark:via-background dark:to-green-900/20">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto mb-4">
            {processing ? (
              <Loader2 className="h-16 w-16 text-primary animate-spin" />
            ) : (
              <CheckCircle className="h-16 w-16 text-green-500" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {processing ? 'Processing Payment...' : 'Payment Successful!'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            {processing 
              ? 'We are unlocking your premium insights. This usually takes a few seconds.'
              : needsReprocess
              ? 'Your payment was successful! Click below to generate your premium insights.'
              : 'Your premium insights have been unlocked successfully!'}
          </p>
          {!processing && !needsReprocess && (
            <Button onClick={() => navigate(`/result?evaluation=${evaluationId}`)}>
              View Premium Insights
            </Button>
          )}
          {needsReprocess && (
            <Button 
              onClick={handleReprocess} 
              disabled={reprocessing}
              className="w-full"
            >
              {reprocessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating Insights...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Generate Premium Insights
                </>
              )}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
